import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowRightLeft, PieChart, Sliders, ScrollText, AlertTriangle } from "lucide-react";
import PortfolioSparkline from "@/components/PortfolioSparkline";

const KEY_STYLE = {
  LONG_TERM: { c: "text-signal-blue", swatch: "bg-signal-blue", desc: "Growth · ETFs · Dividend · 1-10y" },
  SWING: { c: "text-signal-green", swatch: "bg-signal-green", desc: "Breakouts · Pullbacks · days-months" },
  TACTICAL: { c: "text-signal-yellow", swatch: "bg-signal-yellow", desc: "Earnings · News · hours-days" },
};

function fmt(n, d = 2) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });
}

function PortfolioCard({ p, onTransferOpen, onLedger }) {
  const style = KEY_STYLE[p.key] || { c: "text-foreground", swatch: "bg-muted-foreground", desc: "" };
  const pnl = p.unrealized_pnl || 0;
  const tot = p.total_return_pct || 0;
  return (
    <div className="grid-cell !p-0" data-testid={`portfolio-card-${p.key}`}>
      <div className="flex items-center gap-3 px-4 h-10 border-b border-border/60">
        <div className={`w-2 h-2 ${style.swatch}`} />
        <div className="mono uppercase text-sm">{p.name}</div>
        <span className="chip !text-[10px] text-muted-foreground border-current">{p.allocation_pct}%</span>
        <div className="ml-auto flex items-center gap-1">
          <button className="btn-ghost h-7 !px-2 text-[10px]" onClick={() => onTransferOpen(p)} data-testid={`portfolio-transfer-${p.key}`}>
            <ArrowRightLeft size={11} /> Transfer
          </button>
          <button className="btn-ghost h-7 !px-2 text-[10px]" onClick={() => onLedger(p)} data-testid={`portfolio-ledger-${p.key}`}>
            <ScrollText size={11} /> Ledger
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-px bg-border/40">
        <div className="p-3 bg-card/40">
          <div className="label-cap">Allocated Capital</div>
          <div className="mono text-lg">${fmt(p.allocated_capital)}</div>
          <div className="text-[10px] mono uppercase text-muted-foreground mt-1">{style.desc}</div>
        </div>
        <div className="p-3 bg-card/40">
          <div className="label-cap">Available Cash</div>
          <div className="mono text-lg">${fmt(p.available_cash)}</div>
          <div className="text-[10px] mono uppercase text-muted-foreground mt-1">Deployable</div>
        </div>
        <div className="p-3 bg-card/40">
          <div className="label-cap">Invested</div>
          <div className="mono text-lg">${fmt(p.invested_value)}</div>
          <div className="text-[10px] mono uppercase text-muted-foreground mt-1">{p.open_positions || 0} open</div>
        </div>
        <div className="p-3 bg-card/40">
          <div className="label-cap">Unrealized P&L</div>
          <div className={`mono text-lg ${pnl >= 0 ? "pnl-up" : "pnl-down"}`}>
            {pnl >= 0 ? "+" : ""}${fmt(pnl)}
          </div>
          <div className={`text-[10px] mono uppercase mt-1 ${tot >= 0 ? "pnl-up" : "pnl-down"}`}>{tot >= 0 ? "+" : ""}{fmt(tot)}%</div>
        </div>
        <div className="p-3 bg-card/40">
          <div className="label-cap">Realized P&L</div>
          <div className={`mono text-lg ${(p.realized_pnl || 0) >= 0 ? "pnl-up" : "pnl-down"}`}>
            {(p.realized_pnl || 0) >= 0 ? "+" : ""}${fmt(p.realized_pnl)}
          </div>
        </div>
        <div className="p-3 bg-card/40">
          <div className="label-cap">Total Value</div>
          <div className="mono text-lg">${fmt(p.total_value)}</div>
        </div>
      </div>
      <div className="border-t border-border/60 px-3 py-2">
        <PortfolioSparkline portfolioId={p.id} portfolioKey={p.key} defaultRange="1M" height={70}/>
      </div>
      <div className="border-t border-border/60 px-3 py-2 flex items-center gap-2 flex-wrap text-[10px] mono uppercase text-muted-foreground tracking-widest">
        <span>MaxPos {p.risk?.max_position_pct}%</span>
        <span>· MaxRisk/Trade {p.risk?.max_risk_per_trade_pct}%</span>
        <span>· MaxDayLoss {p.risk?.max_daily_loss_pct}%</span>
        <span>· SectorCap {p.risk?.max_sector_pct}%</span>
        <span>· MaxOpen {p.risk?.max_open_positions}</span>
      </div>
    </div>
  );
}

function TransferDialog({ open, onClose, source, portfolios, onDone }) {
  const [dst, setDst] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && source) {
      const first = portfolios.find(p => p.key !== source.key);
      setDst(first?.key || "");
      setAmount("");
    }
  }, [open, source, portfolios]);

  const doTransfer = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    if (amt > (source?.available_cash || 0)) return toast.error("Amount exceeds available cash");
    setBusy(true);
    try {
      await api.post("/portfolios/transfer", { from_key: source.key, to_key: dst, amount: amt });
      toast.success(`Transferred $${fmt(amt)} · ${source.key} → ${dst}`);
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally { setBusy(false); }
  };

  if (!source) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="!rounded-none border-border/70 max-w-md" data-testid="transfer-dialog">
        <DialogHeader><DialogTitle className="mono uppercase tracking-wider text-sm">Transfer Capital</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-px bg-border/40">
            <div className="p-2 bg-card/40"><div className="label-cap">From</div><div className="mono text-sm mt-0.5">{source.name}</div><div className="mono text-xs text-muted-foreground">Avail ${fmt(source.available_cash)}</div></div>
            <div className="p-2 bg-card/40">
              <div className="label-cap">To</div>
              <select value={dst} onChange={(e) => setDst(e.target.value)} className="bg-transparent border border-border/60 h-7 w-full mt-1 mono text-xs px-1" data-testid="transfer-dst-select">
                {portfolios.filter(p => p.key !== source.key).map(p => (
                  <option key={p.key} value={p.key}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <div className="label-cap mb-1">Amount USD</div>
            <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="!rounded-none mono" data-testid="transfer-amount-input" />
          </div>
        </div>
        <DialogFooter>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy || !dst || !amount} onClick={doTransfer} data-testid="transfer-submit-btn">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <ArrowRightLeft size={14} />} Transfer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LedgerDialog({ open, onClose, portfolio }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (open && portfolio) {
      setLoading(true);
      api.get("/portfolios/ledger", { params: { portfolio_id: portfolio.id, limit: 100 } })
        .then(r => setItems(r.data)).finally(() => setLoading(false));
    }
  }, [open, portfolio]);
  if (!portfolio) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="!rounded-none border-border/70 max-w-3xl" data-testid="ledger-dialog">
        <DialogHeader><DialogTitle className="mono uppercase tracking-wider text-sm">{portfolio.name} · Cash Ledger</DialogTitle></DialogHeader>
        {loading ? (
          <div className="p-6 text-center"><Loader2 className="animate-spin inline" size={16} /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground">No ledger entries yet.</div>
        ) : (
          <div className="overflow-auto max-h-96 border border-border/40">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20">
                <tr>
                  <th className="text-left px-2 py-1.5 font-normal">Time</th>
                  <th className="text-left px-2 py-1.5 font-normal">Kind</th>
                  <th className="text-right px-2 py-1.5 font-normal">Amount</th>
                  <th className="text-left px-2 py-1.5 font-normal">Ref</th>
                  <th className="text-left px-2 py-1.5 font-normal">Note</th>
                </tr>
              </thead>
              <tbody>
                {items.map(l => (
                  <tr key={l.id} className="border-t border-border/30">
                    <td className="px-2 py-1.5 mono text-muted-foreground">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="px-2 py-1.5 mono uppercase text-[10px]">{l.kind}</td>
                    <td className={`px-2 py-1.5 mono text-right ${l.amount >= 0 ? "pnl-up" : "pnl-down"}`}>{l.amount >= 0 ? "+" : ""}${fmt(l.amount)}</td>
                    <td className="px-2 py-1.5 mono text-[10px] text-muted-foreground">{(l.ref_id || "").slice(0, 12)}</td>
                    <td className="px-2 py-1.5">{l.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AllocationEditor({ open, onClose, portfolios, onDone }) {
  const [allocs, setAllocs] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      const map = {};
      portfolios.forEach(p => { map[p.key] = p.allocation_pct; });
      setAllocs(map);
    }
  }, [open, portfolios]);
  const total = Object.values(allocs).reduce((s, v) => s + Number(v || 0), 0);
  const submit = async () => {
    if (total > 100.001) return toast.error(`Total ${total.toFixed(1)}% exceeds 100%`);
    setBusy(true);
    try {
      await api.put("/portfolios/allocations", {
        allocations: Object.entries(allocs).map(([key, allocation_pct]) => ({ key, allocation_pct: Number(allocation_pct) })),
      });
      toast.success("Allocations updated");
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="!rounded-none border-border/70 max-w-md" data-testid="allocation-editor">
        <DialogHeader><DialogTitle className="mono uppercase tracking-wider text-sm">Edit Allocations</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {portfolios.map(p => (
            <div key={p.key} className="flex items-center gap-3">
              <div className={`w-2 h-2 ${KEY_STYLE[p.key]?.swatch || "bg-muted-foreground"}`} />
              <div className="mono uppercase text-xs w-24">{p.name}</div>
              <Input type="number" value={allocs[p.key] ?? ""} onChange={(e) => setAllocs({ ...allocs, [p.key]: e.target.value })}
                className="!rounded-none mono flex-1" step="0.1" data-testid={`alloc-input-${p.key}`} />
              <span className="mono text-xs w-4">%</span>
            </div>
          ))}
          <div className="flex items-center justify-between border-t border-border/40 pt-2 text-xs">
            <span className="label-cap">Total</span>
            <span className={`mono ${total > 100.001 ? "pnl-down" : total < 99.999 ? "text-signal-yellow" : "pnl-up"}`}>{total.toFixed(1)}%</span>
          </div>
        </div>
        <DialogFooter>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy || total > 100.001} onClick={submit} data-testid="alloc-save-btn">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Sliders size={14} />} Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PortfolioDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transferSource, setTransferSource] = useState(null);
  const [ledgerPortfolio, setLedgerPortfolio] = useState(null);
  const [editing, setEditing] = useState(false);
  const isOwner = user?.role === "owner";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portfolios");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totalCash = (data?.portfolios || []).reduce((s, p) => s + p.available_cash, 0);
  const totalInvested = (data?.portfolios || []).reduce((s, p) => s + p.invested_value, 0);
  const totalPnl = (data?.portfolios || []).reduce((s, p) => s + p.unrealized_pnl, 0);
  const totalRealized = (data?.portfolios || []).reduce((s, p) => s + (p.realized_pnl || 0), 0);
  const totalAllocated = (data?.portfolios || []).reduce((s, p) => s + (p.allocated_capital || 0), 0);
  const workspaceEquity = Number(data?.workspace_equity || 0);
  // Drift = buckets don't match broker equity. Flag when off by > $500 AND > 5%.
  const drift = totalAllocated - workspaceEquity;
  const driftPct = workspaceEquity > 0 ? Math.abs(drift) / workspaceEquity : 0;
  const showDriftBanner = workspaceEquity > 0 && Math.abs(drift) > 500 && driftPct > 0.05;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="portfolio-dashboard-root">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-medium tracking-tight flex items-center gap-2"><PieChart size={18} /> Portfolios</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Virtual capital buckets. Every trade is funded by — and risk-validated against — a specific portfolio.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {isOwner && (
            <button className="btn-outline" onClick={() => setEditing(true)} data-testid="edit-allocations-btn">
              <Sliders size={14} /> Edit Allocations
            </button>
          )}
        </div>
      </div>

      {/* Bucket-drift banner — buckets total ≠ broker equity */}
      {showDriftBanner && (
        <div className="border border-signal-yellow/60 bg-signal-yellow/5 p-3 flex items-start gap-3" data-testid="bucket-drift-banner">
          <AlertTriangle size={16} className="text-signal-yellow shrink-0 mt-0.5"/>
          <div className="flex-1 text-xs">
            <div className="text-signal-yellow font-medium mb-0.5">Portfolio buckets are out of sync with your broker</div>
            <div className="text-muted-foreground">
              Buckets total <span className="mono text-foreground">${fmt(totalAllocated)}</span> but the broker equity is <span className="mono text-foreground">${fmt(workspaceEquity)}</span>
              <span className="mono"> · drift ${fmt(Math.abs(drift))} ({(driftPct * 100).toFixed(1)}%)</span>.
              Trades sized against these buckets will overshoot your real account.
            </div>
          </div>
          <button
            onClick={() => window.dispatchEvent(new Event("tm:open-rebalance"))}
            className="border border-signal-yellow/60 text-signal-yellow px-3 py-1.5 text-[10px] mono uppercase tracking-widest hover:bg-signal-yellow/10 whitespace-nowrap"
            data-testid="bucket-drift-rebalance-link">
            Sync buckets to broker →
          </button>
        </div>
      )}

      {/* Header stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border/60">
        <div className="p-3 bg-card">
          <div className="label-cap">Workspace Equity</div>
          <div className="mono text-lg mt-0.5">${fmt(data?.workspace_equity)}</div>
        </div>
        <div className="p-3 bg-card">
          <div className="label-cap">Total Deployable Cash</div>
          <div className="mono text-lg mt-0.5">${fmt(totalCash)}</div>
        </div>
        <div className="p-3 bg-card">
          <div className="label-cap">Invested</div>
          <div className="mono text-lg mt-0.5">${fmt(totalInvested)}</div>
        </div>
        <div className="p-3 bg-card">
          <div className="label-cap">Unrealized P&L</div>
          <div className={`mono text-lg mt-0.5 ${totalPnl >= 0 ? "pnl-up" : "pnl-down"}`}>{totalPnl >= 0 ? "+" : ""}${fmt(totalPnl)}</div>
        </div>
        <div className="p-3 bg-card">
          <div className="label-cap">Realized P&L</div>
          <div className={`mono text-lg mt-0.5 ${totalRealized >= 0 ? "pnl-up" : "pnl-down"}`}>{totalRealized >= 0 ? "+" : ""}${fmt(totalRealized)}</div>
        </div>
      </div>

      {/* Allocation bar */}
      {data?.portfolios?.length > 0 && (
        <div className="grid-cell !p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="label-cap">Allocation</span>
            <span className="mono text-[10px] uppercase text-muted-foreground">{data.total_allocation_pct}% allocated</span>
            {data.unallocated_pct > 0 && <span className="chip !text-[10px] text-signal-yellow border-current">{data.unallocated_pct}% unallocated</span>}
          </div>
          <div className="h-2 flex bg-border/40 overflow-hidden">
            {data.portfolios.map(p => (
              <div key={p.key} className={`${KEY_STYLE[p.key]?.swatch || "bg-muted-foreground"}`} style={{ width: `${p.allocation_pct}%` }} title={`${p.name} ${p.allocation_pct}%`} />
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-sm text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} />Loading portfolios…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border/60">
          {(data?.portfolios || []).map(p => (
            <PortfolioCard key={p.id} p={p} onTransferOpen={setTransferSource} onLedger={setLedgerPortfolio} />
          ))}
        </div>
      )}

      <TransferDialog
        open={!!transferSource} onClose={() => setTransferSource(null)}
        source={transferSource} portfolios={data?.portfolios || []}
        onDone={load}
      />
      <LedgerDialog
        open={!!ledgerPortfolio} onClose={() => setLedgerPortfolio(null)}
        portfolio={ledgerPortfolio}
      />
      <AllocationEditor
        open={editing} onClose={() => setEditing(false)}
        portfolios={data?.portfolios || []} onDone={load}
      />
    </div>
  );
}
